import { useState } from 'react';
import { useMutation, useLazyQuery } from '@apollo/client';
import { useDollarJournalDashboardGraphQl } from '@hooks/graphql/useDollarJournalDashboardGraphQl';
import { useDateNavigation } from '@hooks/graphql/useDateNavigation';
import { LOG_EXPORT_AUDIT_EVENT } from 'graphql/audit.gql';
import { NSP_CORE_INFO_BY_TYPE } from '@graphql/nspCoreInfo.gql';
import * as XLSX from 'xlsx';

const useDollarJournalDashboardPage = () => {
  const dateNavigation = useDateNavigation();
  const [logExportAudit] = useMutation(LOG_EXPORT_AUDIT_EVENT);
  const [exportError, setExportError] = useState<string | null>(null);

  // --- Apollo Hook ---
  const {
    data, loading, isAdmin, isManager, isSuperAdmin, isUser, saving, handleEdit, handleSave, saveFeedback,
    asyncApproval, setAsyncApproval,
    isRowOriginallyApproved, username,
    getBulkApproveHeaderState, handleBulkApproveHeaderChange, isSaveDisabled,
    getBulkReadyHeaderState, handleBulkReadyHeaderChange,
  } = useDollarJournalDashboardGraphQl({
    monthYear: dateNavigation.selectedPeriod,
  });

  const [fetchNspCoreInfo] = useLazyQuery(NSP_CORE_INFO_BY_TYPE);

  const handleExportToExcel = async () => {
    if (!data || data.length === 0) return;

    setExportError(null);

    // Title row
    const titleRow = [`Journal Entry Export for: ${dateNavigation.monthYearLabel}`, '', '', '', '', ''];

    // Header row
    const headerRow = ['GL', 'Debit', 'Credit', 'Line Description', 'Approver', 'Approved At'];

    // One journal line: positive value -> Debit column, negative -> Credit column (absolute value).
    const buildLine = (gl: string | null | undefined, value: number, description: string, approver: string, approvedAt: string) => {
      const debit = value >= 0 ? value : '';
      const credit = value < 0 ? Math.abs(value) : '';
      return [gl ?? '', debit, credit, description, approver, approvedAt];
    };

    // Forces a line onto the Debit column regardless of the value's stored sign.
    const buildDebitLine = (gl: string | null | undefined, value: number, description: string, approver: string, approvedAt: string) =>
      [gl ?? '', Math.abs(value), '', description, approver, approvedAt];

    // Forces a line onto the Credit column regardless of the value's stored sign.
    const buildCreditLine = (gl: string | null | undefined, value: number, description: string, approver: string, approvedAt: string) =>
      [gl ?? '', '', Math.abs(value), description, approver, approvedAt];

    // Data rows: one line per meter posts the full dollar_value (SPP Cost) to dollar_gl_number,
    // sign -> Debit/Credit — same for every meter, Counter Party or not. Counter Party meters get
    // an ADDITIONAL line for the counter_party_fee (= -(dollar_value * cib_percentage / 100), see
    // lib/meterCostCalc.js), exactly like Admin Fee and Forecast Fee below: it never reduces the
    // primary vendor's own SPP Cost line, only adds its own line, so "SPP Cost instead of Invoice
    // Cost" holds for every meter (previously this line net­ted counter_party_fee into the primary
    // share, which numerically collapsed to Invoice Cost whenever admin_fee/forecast_fee were 0).
    // The counter party's own share's GL/column depends on secondary_vendor_type:
    //  - NSPI: Credit, on secondary_ipp_gl_number (the NSPI GL associated with the primary vendor).
    //  - CIB (default when type is unset): Debit, on dollar_gl_number (Primary Vendor GL), even
    //    though counter_party_fee is stored as a negative amount on the Meter Details page.
    // Admin Fee and Forecast Fee always post to the Credit column (both are stored as positive
    // magnitudes on the Meter Details page for CIB and NSPI vendors alike), independent of Counter
    // Party status, since they're meter-level costs unrelated to the Counter Party split.
    const dataRows = data.flatMap((row) => {
      const approver = row.journal_approver_signature ?? '';
      const approvedAt = row.journal_posted_date_time
        ? new Date(row.journal_posted_date_time).toLocaleString()
        : '';
      const description = row.location ?? '';
      const dollarValue = row.dollar_value ?? 0;
      const counterPartyFee = row.counter_party_fee;

      const lines: (string | number)[][] = [];

      if (counterPartyFee != null) {
        const counterPartyShare = Math.abs(counterPartyFee);
        const isNspi = row.secondary_vendor_type === 'NSPI';
        lines.push(buildLine(row.dollar_gl_number, dollarValue, `${description} - Primary Vendor`, approver, approvedAt));
        lines.push(
          isNspi
            ? buildCreditLine(row.secondary_ipp_gl_number, counterPartyShare, `${description} - Counter Party`, approver, approvedAt)
            : buildDebitLine(row.dollar_gl_number, counterPartyShare, `${description} - Counter Party`, approver, approvedAt)
        );
      } else {
        lines.push(buildLine(row.dollar_gl_number, dollarValue, description, approver, approvedAt));
      }

      if (row.admin_fee != null && row.admin_fee_gl_number) {
        lines.push(buildCreditLine(row.admin_fee_gl_number, row.admin_fee, `${description} - Admin Fee`, approver, approvedAt));
      }

      if (row.forecast_fee != null && row.forecast_fee_gl_number) {
        lines.push(buildCreditLine(row.forecast_fee_gl_number, row.forecast_fee, `${description} - Forecast Fee`, approver, approvedAt));
      }

      return lines;
    });

    // Summary row — totals reflect every journal line above (base cost split + fee lines),
    // not just the raw per-meter dollar_value, so the sheet stays internally consistent.
    const totalDebit = dataRows.reduce((sum, r) => sum + (typeof r[1] === 'number' ? r[1] : 0), 0);
    const totalCredit = dataRows.reduce((sum, r) => sum + (typeof r[2] === 'number' ? r[2] : 0), 0);
    const netTotal = totalDebit - totalCredit;
    const { data: nspCoreInfoData } = await fetchNspCoreInfo({ variables: { entryType: 'PRIMARY' } });
    const nspGl = nspCoreInfoData?.nspCoreInfoByType?.nspGl ?? '';
    const summaryRow = [nspGl, '', netTotal || '', `To Accrue purchases`, '', ''];

    // Build worksheet
    const worksheetData = [
      titleRow,
      headerRow,
      ...dataRows,
      summaryRow,
    ];
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths
    ws['!cols'] = [
      { wch: 15 },  // GL
      { wch: 15 },  // Debit
      { wch: 15 },  // Credit
      { wch: 40 },  // Line Description
      { wch: 25 },  // Approver
      { wch: 20 },  // Approved At
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, ws, 'Journal Entry');

    const filename = `Dollar_Journal_Dashboard_${dateNavigation.monthYearLabel.replaceAll(' ', '_')}.xlsx`;
    XLSX.writeFile(workbook, filename);

    logExportAudit({
      variables: { dashboard: 'DollarJournalDashboard', monthYear: dateNavigation.selectedPeriod },
      // eslint-disable-next-line no-console
    }).catch((err) => console.error('Failed to log export audit event:', err));
  };

  return {
    ...dateNavigation,
    data,
    loading,
    isAdmin,
    isSuperAdmin,
    isManager,
    isUser,
    saving,
    handleEdit,
    handleSave,
    saveFeedback,
    asyncApproval,
    setAsyncApproval,
    isRowOriginallyApproved,
    username,
    handleExportToExcel,
    exportError,
    getBulkApproveHeaderState,
    handleBulkApproveHeaderChange,
    isSaveDisabled,
    getBulkReadyHeaderState,
    handleBulkReadyHeaderChange,
  };
};

export default useDollarJournalDashboardPage;
